import {
  object,
  collection,
  number,
  boolean,
  string,
  date,
  array,
} from '@gogame/schemas';

import {
  campaignsSchema,
  campaignNameSchema,
  createPlatformCampaign,
  cleanPlatformCampaign,
} from 'scenes/Campaigns/components/Form/Campaign/schema';
import { costModelSchema } from 'scenes/Campaigns/components/CostModel/schema';

import {
  CAMPAIGN_PLATFORM_ID,
  CAMPAIGN_ALTERNATIVE_ID,
  CAMPAIGN_NAME,
  CAMPAIGN_COST_MODELS,
  GENERATE_NAME_AUTOMATICALLY,
} from 'scenes/Campaigns/components/Form/Campaign/constants';
import {
  COST_MODELS,
  CAMPAIGNS,
  APPLICATION,
  PLATFORM,
  RE_ENGAGEMENT,
  AFFILIATE,
  NETWORK,
  DEEP_LINK,
  COUNTRY,
  OWNER,
  LINK,
  LINK_EXPIRATION_DATE,
  TEST_CAMPAIGN_END_DATE,
  TEST_CAMPAIGN_ID,
  TYPE,
  MEDIA_SOURCE,
  LINK_TYPE_ID,
  TRACKING_TYPE,
  LINK_TYPE,
  DEFAULT_TYPE,
  ADDITIONAL_PARAMS,
  RATE_VALUE,
  RATE_CURRENCY,
  RATE_PERIOD,
  LABELS,
  MISSING_MEDIA_SOURCE,
  CTV_LINK,
} from 'scenes/Campaigns/components/Form/constants';

export const deepLinkActivateSchema = object({
  [RE_ENGAGEMENT]: boolean().oneOf([true]),
  [APPLICATION]: number().required(),
  [PLATFORM]: number().required(),
  [AFFILIATE]: number().required(),
});

export const linksActivateSchema = object({
  [RE_ENGAGEMENT]: boolean(),
  [APPLICATION]: number().required(),
  [PLATFORM]: number().required(),
  [AFFILIATE]: number().required(),
  [NETWORK]: number().required(),
});

const baseCampaignSchema = object({
  [COST_MODELS]: collection(costModelSchema),
  [AFFILIATE]: number().required('Affiliate is required'),
  [APPLICATION]: number().required('Application is required'),
  [NETWORK]: number().required('Network is required'),
  [PLATFORM]: number().required('Platform is required'),
  [COUNTRY]: number().required('Country is required'),
  [MISSING_MEDIA_SOURCE]: boolean(),
  [MEDIA_SOURCE]: string().when(MISSING_MEDIA_SOURCE, {
    is: true,
    then: string().required('Media source is required'),
  }),
  [LINK_EXPIRATION_DATE]: date(),
  [LINK_TYPE_ID]: number().default(TRACKING_TYPE),
  [LINK_TYPE]: object({
    id: number(),
    name: string(),
  }).default(DEFAULT_TYPE),
});

export const multiCampaignsSchema = baseCampaignSchema.shape({
  [CAMPAIGNS]: campaignsSchema,
  [TEST_CAMPAIGN_END_DATE]: date().when(TEST_CAMPAIGN_ID, {
    is: (val) => val,
    then: date().required('Stats collection start date is required'),
  }),
  [TYPE]: number().default(1),
});

export const simpleCampaignsSchema = baseCampaignSchema.shape({
  [CAMPAIGN_NAME]: campaignNameSchema,
  [CAMPAIGN_ALTERNATIVE_ID]: string(),
});

export function createMultiCampaigns(userId) {
  return multiCampaignsSchema.cast(
    {
      [OWNER]: userId,
    },
    { context: {} }
  );
}

export const dummyCampaignSchema = object({
  [CAMPAIGN_NAME]: string().when(
    [GENERATE_NAME_AUTOMATICALLY],
    (isNameGeneratedAutomatically, schema) => {
      if (!isNameGeneratedAutomatically) {
        return schema.required('Campaign name is required');
      }
      return schema;
    }
  ),
  [APPLICATION]: number().required('Application is required'),
  [NETWORK]: number().required('Network is required'),
  [AFFILIATE]: number().required('Affiliate is required'),
  [PLATFORM]: number().required('Platform is required'),
  [COUNTRY]: number().required('Country is required'),
  [OWNER]: number().required('Owner is required'),
  [RATE_VALUE]: number().required('Rate is required'),
  [RATE_CURRENCY]: string().required('Currency is required'),
  [LABELS]: array(),
  [RATE_PERIOD]: array().required('Period is required'),
  [TYPE]: number().default(1),
});

export function createDummyCampaign(data = {}) {
  return dummyCampaignSchema.cast(
    {
      [CAMPAIGN_NAME]: '',
      [GENERATE_NAME_AUTOMATICALLY]: true,
      [APPLICATION]: data.application,
      [NETWORK]: data.network,
      [AFFILIATE]: data.affiliate,
      [PLATFORM]: data.platform,
      [COUNTRY]: data.country,
      [OWNER]: data.ownerId,
      [RATE_VALUE]: data.amount,
      [RATE_CURRENCY]: data.currency,
      [RATE_PERIOD]: data.period,
    },
    { context: {} }
  );
}

export function duplicateCampaign({
  [CAMPAIGN_NAME]: campaignName,
  [CAMPAIGN_ALTERNATIVE_ID]: alternativeId,
  [COST_MODELS]: costModels,
  [DEEP_LINK]: platformDeepLink,
  [LINK]: link,
  [CTV_LINK]: _ctvLink, // omitted ctvLink,
  ...campaign
}) {
  return multiCampaignsSchema.cast(
    {
      ...campaign,
      [CAMPAIGNS]: [
        createPlatformCampaign({
          [CAMPAIGN_COST_MODELS]: costModels,
          [CAMPAIGN_PLATFORM_ID]: campaign[PLATFORM],
          [CAMPAIGN_NAME]: `${campaignName} the copy`,
          [LINK]: { ...link, [ADDITIONAL_PARAMS]: '' },
          [CAMPAIGN_ALTERNATIVE_ID]: alternativeId,
        }),
      ],
      [CAMPAIGN_ALTERNATIVE_ID]: undefined,
      [DEEP_LINK]: platformDeepLink,
      [LINK_TYPE_ID]: TRACKING_TYPE,
      [LINK_TYPE]: DEFAULT_TYPE,
    },
    {
      context: {
        stripPeriodFinancialResultId: true,
        stripPeriodReadonly: true,
      },
    }
  );
}

export function duplicateMultiCampaigns({
  [CAMPAIGNS]: campaigns,
  ...multiCampaigns
}) {
  return multiCampaignsSchema.cast(
    {
      ...multiCampaigns,
      [CAMPAIGNS]: campaigns.map((campaign) => ({
        ...cleanPlatformCampaign(campaign),
        [CAMPAIGN_NAME]: `${campaign[CAMPAIGN_NAME]} the copy`,
      })),
    },
    { context: {} }
  );
}
